// v3.18-1347 PAYROLL-001 — My Money Manager (personal employee dashboard).
//
// SSOT: reads from `compensation_ledger`. No sync, no copying — this page
// and the master Money Manager view are two windows onto the same
// collection.
//
// Layout:
//   • 7 tiles: MTD · YTD · Pending Approval · Approved · Pending Payment
//               · Paid This Month · Paid This Year
//   • Monthly History accordion (Jan → Dec of the current year)
//   • Deal History table with full lifecycle metadata

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Wallet, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
  });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso.length === 10 ? iso + 'T00:00:00' : iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const MONTHS = [
  'January', 'February', 'March',     'April',
  'May',     'June',     'July',      'August',
  'September','October', 'November',  'December',
];

const Tile = ({ label, amount, accent = 'blue', testId }) => (
  <div
    data-testid={testId}
    className={
      'rounded-2xl bg-white p-4 shadow-sm border-2 ' +
      (accent === 'emerald' ? 'border-emerald-100' :
        accent === 'amber' ? 'border-amber-100' :
        accent === 'violet' ? 'border-violet-100' :
        accent === 'slate' ? 'border-slate-200' :
        accent === 'sky' ? 'border-sky-100' :
        accent === 'rose' ? 'border-rose-100' :
        'border-blue-100')
    }
  >
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className={
      'text-2xl font-extrabold mt-1 ' +
      (accent === 'emerald' ? 'text-emerald-700' :
        accent === 'amber' ? 'text-amber-700' :
        accent === 'violet' ? 'text-violet-700' :
        accent === 'slate' ? 'text-slate-700' :
        accent === 'sky' ? 'text-sky-700' :
        accent === 'rose' ? 'text-rose-700' :
        'text-blue-700')
    }>{fmtMoney(amount)}</div>
  </div>
);

const normalizeStatus = (s) => {
  const raw = (s || 'pending').toLowerCase();
  if (raw === 'pending') return 'pending_approval';
  return raw;
};

const StatusPill = ({ status }) => {
  const s = normalizeStatus(status);
  const label = s.replaceAll('_', ' ');
  const cls =
    s === 'paid'            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    s === 'pending_payment' ? 'bg-sky-50 text-sky-700 border-sky-200' :
    s === 'approved'        ? 'bg-violet-50 text-violet-700 border-violet-200' :
    s === 'voided'          ? 'bg-slate-100 text-slate-600 border-slate-200' :
                              'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span
      data-testid={`ledger-status-${s}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border capitalize ${cls}`}
    >{label}</span>
  );
};

export default function MyMoneyManagerPage() {
  const [loading, setLoading]   = useState(true);
  const [items, setItems]       = useState([]);
  const [summary, setSummary]   = useState({
    pending_approval: 0, approved: 0, pending_payment: 0,
    paid_this_month: 0, paid_this_year: 0,
    mtd: 0, ytd: 0, month_to_date: 0, year_to_date: 0,
    lifetime: 0, count: 0,
  });
  const [openMonth, setOpenMonth] = useState(null);

  const currentYear = new Date().getFullYear();

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/compensation-ledger/mine', {
        params: { year: currentYear, limit: 1000 },
      });
      setItems(data?.items || []);
      setSummary(data?.summary || {});
    } catch (e) {
      toast.error('Failed to load your money manager');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const byMonth = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const ev = r.event_date || '';
      const m = ev.length >= 7 ? parseInt(ev.slice(5, 7), 10) - 1 : 0;
      if (!map.has(m)) map.set(m, []);
      map.get(m).push(r);
    }
    return map;
  }, [items]);

  const hasAnyData = (summary?.count || 0) > 0 || items.length > 0;

  return (
    <div className="space-y-4" data-testid="my-money-manager-page">
      <div className="flex items-center gap-3">
        <Wallet className="w-6 h-6 text-blue-600" />
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">My Money Manager</h1>
          <p className="text-sm text-slate-500">
            Your personal financial dashboard — every earning, bonus, override, and payment.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : !hasAnyData ? (
        <div
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center"
          data-testid="my-money-manager-empty-state"
        >
          <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800">
            You have no earnings yet.
          </h2>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Earnings will appear here automatically the moment Payroll assigns
            a commission or bonus to you.
          </p>
        </div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Tile label="Month to Date"    amount={summary.month_to_date ?? summary.mtd}          testId="tile-mtd" />
            <Tile label="Year to Date"     amount={summary.year_to_date ?? summary.ytd} accent="violet"  testId="tile-ytd" />
            <Tile label="Pending Approval" amount={summary.pending_approval}             accent="amber"   testId="tile-pending-approval" />
            <Tile label="Approved"         amount={summary.approved}                     accent="violet"  testId="tile-approved" />
            <Tile label="Pending Payment"  amount={summary.pending_payment}              accent="sky"     testId="tile-pending-payment" />
            <Tile label="Paid This Month"  amount={summary.paid_this_month}              accent="emerald" testId="tile-paid-this-month" />
            <Tile label="Paid This Year"   amount={summary.paid_this_year}               accent="emerald" testId="tile-paid-this-year" />
          </div>

          {/* Monthly History accordion */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="monthly-history">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                Monthly History — {currentYear}
              </h3>
            </div>
            <div className="divide-y divide-slate-100">
              {MONTHS.map((name, idx) => {
                const rows = byMonth.get(idx) || [];
                const monthTotal = rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
                const isOpen = openMonth === idx;
                return (
                  <div key={name} data-testid={`monthly-history-row-${idx + 1}`}>
                    <button
                      type="button"
                      onClick={() => setOpenMonth(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 text-left"
                      data-testid={`monthly-history-toggle-${idx + 1}`}
                    >
                      <div className="flex items-center gap-2">
                        {isOpen
                          ? <ChevronDown className="w-4 h-4 text-slate-400" />
                          : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <span className="text-sm font-semibold text-slate-800">{name}</span>
                        <span className="text-xs text-slate-500">
                          {rows.length} {rows.length === 1 ? 'earning' : 'earnings'}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-slate-700">{fmtMoney(monthTotal)}</span>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 bg-slate-50/50">
                        {rows.length === 0 ? (
                          <div className="py-3 text-center text-xs text-slate-400 italic">No earnings this month.</div>
                        ) : (
                          <DealHistoryTable rows={rows} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Full Deal History table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="deal-history">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                Deal History — All {items.length} Earnings
              </h3>
            </div>
            {items.length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="deal-history-empty">
                No earnings on record.
              </div>
            ) : (
              <DealHistoryTable rows={items} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DealHistoryTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="deal-history-table">
        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">Created</th>
            <th className="text-left px-3 py-2">Client / Deal</th>
            <th className="text-left px-3 py-2">Company</th>
            <th className="text-left px-3 py-2">Commission Type</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-center px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Notes</th>
            <th className="text-left px-3 py-2">Paid Date</th>
            <th className="text-left px-3 py-2">Payment Ref</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.compensation_event_id}
                data-testid={`ledger-row-${r.compensation_event_id}`}
                className="border-t border-slate-100 hover:bg-slate-50 align-top">
              <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                {fmtDate(r.created_at || r.event_date)}
              </td>
              <td className="px-3 py-2 text-slate-800">
                {r.deal_name || r.client_name || r.deal_id ||
                  <span className="text-slate-400 italic">—</span>}
              </td>
              <td className="px-3 py-2 text-slate-600">
                {r.company_name || <span className="text-slate-400 italic">—</span>}
              </td>
              <td className="px-3 py-2 text-slate-700">
                {r.commission_type ||
                  ((r.event || '').split('.').pop() || '')
                    .replaceAll('_', ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
              </td>
              <td className="px-3 py-2 text-right font-semibold text-slate-900">
                {fmtMoney(r.amount)}
              </td>
              <td className="px-3 py-2 text-center">
                <StatusPill status={r.status} />
              </td>
              <td className="px-3 py-2 text-slate-600 max-w-[240px] truncate">
                {r.note || <span className="text-slate-300">—</span>}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                {r.paid_date ? fmtDate(r.paid_date) : <span className="text-slate-300">—</span>}
              </td>
              <td className="px-3 py-2 font-mono text-[11px] text-slate-500">
                {r.payment_reference || <span className="text-slate-300">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
